import type { Entity } from '../ecs/entity'
import type { World } from '../ecs/world'
import { TILE } from '../config/tuning'
import { Tile } from '../world/tiles'
import { norm } from '../core/math'
import { makeEnemy } from '../entities/templates'
import { PALETTE } from '../render/palette'
import { hurtPlayer } from './damage'

/**
 * Parameterized boss framework. Bosses are data (BossDef) on top of a shared
 * telegraph → attack → recovery cycle with phase thresholds and add-spawning
 * hooks. TELEGRAPHS ARE MANDATORY: every attack begins in 'telegraph' and the
 * renderer draws it — there is no code path that attacks without one.
 */
export function bossSystem(world: World, dt: number): void {
  for (const e of world.entities) {
    if (e.dead || e.kind !== 'boss' || !e.boss) continue
    const b = e.boss
    const def = b.def
    const player = world.nearestPlayer(e.x, e.y)
    if (b.touchT > 0) b.touchT -= dt
    if (e.hp !== undefined && e.hp <= 0) {
      dieBoss(world, e)
      continue
    }

    // phase transitions on hp thresholds
    const frac = e.hp !== undefined && e.maxHp !== undefined ? e.hp / e.maxHp : 1
    let targetPhase = 0
    for (let i = 0; i < def.phases.length; i++) {
      if (frac <= def.phases[i]!.threshold) targetPhase = i
    }
    if (targetPhase > b.phase) {
      b.phase = targetPhase
      b.attackIndex = 0
      b.addT = 0
      world.addShake(world.tuning.feel.shakeSmall)
      world.burst(e.x, e.y, PALETTE.alarmRed, 18, 200, 0.6)
      world.bus.emit('boss_phase', { bossId: def.id, phase: targetPhase + 1 })
    }
    const phase = def.phases[b.phase]!

    // add-spawning hook (reuses the generator concept without a generator entity)
    if (phase.addSpawning) {
      b.addT -= dt
      if (b.addT <= 0) {
        b.addT = phase.addSpawning.interval
        const alive = world.entities.filter((x) => !x.dead && x.parentGen === e.id).length
        if (alive < phase.addSpawning.aliveCap) {
          const ang = world.levelRng.range(0, Math.PI * 2)
          const child = makeEnemy(world.tuning, phase.addSpawning.type, e.x + Math.cos(ang) * (e.radius + 18), e.y + Math.sin(ang) * (e.radius + 18), e.id, world.level.band)
          world.add(child)
          world.burst(e.x, e.y, PALETTE.generatorCore, 6, 110, 0.4)
        }
      }
    }

    b.stageT += dt
    switch (b.stage) {
      case 'idle': {
        // stalk the player slowly, keep facing them
        if (player) {
          const d = norm({ x: player.x - e.x, y: player.y - e.y })
          e.facing = Math.atan2(d.y, d.x)
          world.map.moveCircle(e, d.x * def.speed * dt, d.y * def.speed * dt, true)
        }
        if (b.stageT >= phase.attackInterval && player) {
          b.currentAttack = phase.attacks[b.attackIndex % phase.attacks.length]!
          b.attackIndex++
          b.stage = 'telegraph'
          b.stageT = 0
          // lock aim at telegraph start — dodgeable by design
          const d = norm({ x: player.x - e.x, y: player.y - e.y })
          b.ax = d.x
          b.ay = d.y
          e.facing = Math.atan2(d.y, d.x)
        }
        break
      }
      case 'telegraph': {
        const tt = def.params.telegraph ?? world.tuning.boss.telegraphTime
        if (b.stageT >= tt) {
          b.stage = 'attack'
          b.stageT = 0
          if (b.currentAttack === 'slam') {
            doSlam(world, e)
            b.stage = 'recover'
          }
        }
        break
      }
      case 'attack': {
        if (b.currentAttack === 'charge') {
          const speed = def.params.chargeSpeed ?? world.tuning.boss.chargeSpeed
          const beforeX = e.x
          const beforeY = e.y
          // breakable cover in the charge path shatters before the move
          // resolves — the boss smashes through pillars; real walls still stop it
          smashDestructibles(world, e.x + b.ax * (e.radius + 6), e.y + b.ay * (e.radius + 6), e.radius, world.tuning.boss.wallDamage.charge)
          world.map.moveCircle(e, b.ax * speed * dt, b.ay * speed * dt, true)
          const moved = Math.hypot(e.x - beforeX, e.y - beforeY)
          touchDamage(world, e, player)
          // wall impact or spent distance ends the charge
          if (moved < speed * dt * 0.35 || b.stageT > 0.9) {
            if (moved < speed * dt * 0.35) {
              world.addShake(world.tuning.feel.shakeBig)
              world.burst(e.x + b.ax * e.radius, e.y + b.ay * e.radius, PALETTE.wallTop, 14, 160, 0.5)
            }
            b.stage = 'recover'
            b.stageT = 0
          }
        } else {
          b.stage = 'recover'
          b.stageT = 0
        }
        break
      }
      case 'recover': {
        if (b.stageT >= world.tuning.boss.recoveryTime) {
          b.stage = 'idle'
          b.stageT = 0
        }
        break
      }
    }

    if (b.stage !== 'attack') touchDamage(world, e, player)
  }
}

function touchDamage(world: World, e: Entity, player: Entity | null): void {
  if (!player || player.dead) return
  const b = e.boss!
  if (b.touchT > 0) return
  const r = e.radius + player.radius
  if ((e.x - player.x) ** 2 + (e.y - player.y) ** 2 >= r * r) return
  hurtPlayer(world, player, world.tuning.boss.contactDamage, b.def.name.toLowerCase())
  b.touchT = world.tuning.boss.touchCooldown
}

function doSlam(world: World, e: Entity): void {
  const b = e.boss!
  const radius = (b.def.params.slamRadius ?? world.tuning.boss.slamRadius) * (b.def.params.scale ?? 1)
  const dmg = b.def.params.slamDamage ?? world.tuning.boss.slamDamage
  // dead zone at the epicenter — hugging the boss beats the shockwave
  const safe = e.radius + world.tuning.boss.slamInnerSafe
  world.addShake(world.tuning.feel.shakeBig)
  world.burst(e.x, e.y, PALETTE.alarmRed, 30, 260, 0.7, 4)
  for (const p of world.players()) {
    const d2 = (p.x - e.x) ** 2 + (p.y - e.y) ** 2
    if (d2 > safe * safe && d2 <= radius * radius) {
      hurtPlayer(world, p, dmg, `${b.def.name.toLowerCase()} shockwave`)
    }
  }
  // the shockwave cracks breakable cover too — tiles get no inner dead zone
  // (the dead zone is an escape rule for the player, not masonry)
  smashDestructibles(world, e.x, e.y, radius, world.tuning.boss.wallDamage.slam)
}

/**
 * Boss-attack erosion of destructible walls: apply `hits` wall-damage ticks
 * to every destructible tile within r of (x, y), with the crumble FX the
 * player already knows from shooting them. DECISION: bosses currently fire
 * no projectiles, and network projectiles deliberately do NOT chip
 * destructibles — sentry fire eroding puzzle walls (BLACKSITE's relay pocket,
 * secret stashes) would quietly break authored puzzle assumptions.
 */
function smashDestructibles(world: World, x: number, y: number, r: number, hits: number): void {
  const minTx = Math.floor((x - r) / TILE)
  const maxTx = Math.floor((x + r) / TILE)
  const minTy = Math.floor((y - r) / TILE)
  const maxTy = Math.floor((y + r) / TILE)
  for (let ty = minTy; ty <= maxTy; ty++) {
    for (let tx = minTx; tx <= maxTx; tx++) {
      if (world.map.at(tx, ty) !== Tile.Destructible) continue
      const cx = Math.max(tx * TILE, Math.min((tx + 1) * TILE, x))
      const cy = Math.max(ty * TILE, Math.min((ty + 1) * TILE, y))
      if ((x - cx) ** 2 + (y - cy) ** 2 > r * r) continue
      for (let i = 0; i < hits; i++) {
        if (world.map.damageWall(tx, ty)) {
          world.burst(tx * TILE + TILE / 2, ty * TILE + TILE / 2, PALETTE.rubble, 14, 120, 0.6)
          break
        }
      }
    }
  }
}

function dieBoss(world: World, e: Entity): void {
  e.dead = true
  const b = e.boss!
  world.addScore(world.tuning.score.bossKill)
  world.addShake(world.tuning.feel.shakeBig * 1.4)
  world.burst(e.x, e.y, PALETTE.alarmRed, 50, 320, 1.1, 5)
  world.burst(e.x, e.y, PALETTE.treasure, 24, 220, 0.9, 3)
  // its adds stand down
  for (const child of world.entities) {
    if (!child.dead && child.parentGen === e.id) {
      child.dead = true
      world.burst(child.x, child.y, PALETTE.enemyBurst[child.etype ?? 'husk'] ?? PALETTE.hostile, 6, 100, 0.4)
    }
  }
  world.exitOpen = true
  world.bossActive = false
  world.bus.emit('boss_kill', { bossId: b.def.id, name: b.def.name })
}
